import React, { useState, useEffect, useRef } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { MapPin, Search, X, Navigation } from 'lucide-react';

interface GoogleMapsSearchProps {
  onLocationSelect: (location: { latitude: number; longitude: number; address: string }) => void;
  initialLocation?: { latitude: number; longitude: number };
  placeholder?: string;
  showMap?: boolean;
  mapHeight?: string;
}

export const GoogleMapsSearch: React.FC<GoogleMapsSearchProps> = ({
  onLocationSelect,
  initialLocation,
  placeholder,
  showMap = true,
  mapHeight = '300px',
}) => {
  const { t } = useLanguage();
  const [searchQuery, setSearchQuery] = useState('');
  const [suggestions, setSuggestions] = useState<any[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState<{ latitude: number; longitude: number; address: string } | null>(null);
  
  const mapRef = useRef<HTMLDivElement>(null);
  const mapInstance = useRef<google.maps.Map | null>(null);
  const marker = useRef<google.maps.Marker | null>(null);
  const isInitialized = useRef(false);
  const onLocationSelectRef = useRef(onLocationSelect);

  // Update the ref when onLocationSelect changes
  useEffect(() => {
    onLocationSelectRef.current = onLocationSelect;
  }, [onLocationSelect]);

  useEffect(() => {
    // Initialize Google Maps services only once
    const initializeMap = () => {
      if (window.google && window.google.maps && !isInitialized.current && mapRef.current) {
        try {
          // Check if initialLocation has valid coordinates (not 0,0)
          const hasValidInitialLocation = initialLocation && 
            initialLocation.latitude !== 0 && 
            initialLocation.longitude !== 0 &&
            Math.abs(initialLocation.latitude) <= 90 &&
            Math.abs(initialLocation.longitude) <= 180;
          
          const defaultCenter = hasValidInitialLocation
            ? { lat: initialLocation.latitude, lng: initialLocation.longitude }
            : { lat: 41.9028, lng: 12.4964 }; // Rome as default
          
          mapInstance.current = new google.maps.Map(mapRef.current, {
            center: defaultCenter,
            zoom: hasValidInitialLocation ? 15 : 10,
            mapTypeControl: true,
            streetViewControl: true,
            fullscreenControl: true,
            zoomControl: true,
            gestureHandling: 'greedy',
            clickableIcons: true,
          });
          
          // Add click listener to map for manual location selection
          mapInstance.current.addListener('click', (event: google.maps.MapMouseEvent) => {
            if (event.latLng) {
              const lat = event.latLng.lat();
              const lng = event.latLng.lng();
              
              // Update marker position directly without calling updateMarkerPosition
              if (marker.current) {
                marker.current.setPosition({ lat, lng });
              } else {
                marker.current = new google.maps.Marker({
                  position: { lat, lng },
                  map: mapInstance.current,
                  draggable: true,
                  title: 'Selected Location',
                });

                // Add drag listener to marker
                marker.current.addListener('dragend', (event: google.maps.MapMouseEvent) => {
                  if (event.latLng) {
                    const newLat = event.latLng.lat();
                    const newLng = event.latLng.lng();
                    
                    const location = { 
                      latitude: newLat, 
                      longitude: newLng, 
                      address: `${newLat.toFixed(6)}, ${newLng.toFixed(6)}` 
                    };
                    setSelectedLocation(location);
                    setSearchQuery(location.address);
                    onLocationSelectRef.current(location);
                  }
                });
              }

              // Center map on marker
              if (mapInstance.current) {
                mapInstance.current.setCenter({ lat, lng });
              }
              
              // Update location with coordinates only
              const location = { 
                latitude: lat, 
                longitude: lng, 
                address: `${lat.toFixed(6)}, ${lng.toFixed(6)}` 
              };
              setSelectedLocation(location);
              setSearchQuery(location.address);
              onLocationSelectRef.current(location);
            }
          });
          
          isInitialized.current = true;
          console.log('Map initialized successfully');
        } catch (error) {
          console.error('Error initializing map:', error);
        }
      }
    };

    // Try to initialize immediately
    initializeMap();

    // If Google Maps isn't loaded yet, wait for it
    if (!window.google || !window.google.maps) {
      const checkGoogleMaps = setInterval(() => {
        if (window.google && window.google.maps) {
          clearInterval(checkGoogleMaps);
          initializeMap();
        }
      }, 100);

      // Cleanup interval after 10 seconds
      setTimeout(() => clearInterval(checkGoogleMaps), 10000);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // No dependencies to prevent loops


  useEffect(() => {
    // Only update marker if we have valid coordinates and map is ready
    if (initialLocation && 
        mapInstance.current && 
        initialLocation.latitude !== 0 && 
        initialLocation.longitude !== 0 &&
        Math.abs(initialLocation.latitude) <= 90 &&
        Math.abs(initialLocation.longitude) <= 180) {
      
      const lat = initialLocation.latitude;
      const lng = initialLocation.longitude;
      
      // Update marker position directly
      if (marker.current) {
        marker.current.setPosition({ lat, lng });
      } else {
        marker.current = new google.maps.Marker({
          position: { lat, lng },
          map: mapInstance.current,
          draggable: true,
          title: 'Selected Location',
        });

        // Add drag listener to marker
        marker.current.addListener('dragend', (event: google.maps.MapMouseEvent) => {
          if (event.latLng) {
            const newLat = event.latLng.lat();
            const newLng = event.latLng.lng();
            
            const location = { 
              latitude: newLat, 
              longitude: newLng, 
              address: `${newLat.toFixed(6)}, ${newLng.toFixed(6)}` 
            };
            setSelectedLocation(location);
            setSearchQuery(location.address);
            onLocationSelectRef.current(location);
          }
        });
      }

      // Center map on marker
      mapInstance.current.setCenter({ lat, lng });
      
      // Update location with coordinates only (reflect prop change locally; avoid notifying parent)
      const location = { 
        latitude: lat, 
        longitude: lng, 
        address: `${lat.toFixed(6)}, ${lng.toFixed(6)}` 
      };
      setSelectedLocation(location);
      setSearchQuery(location.address);
    }
  }, [initialLocation]);

  const handleSearch = async (query: string) => {
    if (!query.trim()) {
      setSuggestions([]);
      setShowSuggestions(false);
      return;
    }

    setIsLoading(true);
    
    try {
      // Use a simple geocoding approach instead of Places API
      const geocoder = new google.maps.Geocoder();
      geocoder.geocode({ address: query }, (results: any, status: any) => {
        setIsLoading(false);
        
        if (status === 'OK' && results && results.length > 0) {
          // Convert geocoding results to suggestion format
          const suggestions = results.slice(0, 5).map((result: any) => ({
            description: result.formatted_address,
            place_id: result.place_id,
            structured_formatting: {
              main_text: result.address_components[0]?.long_name || result.formatted_address,
              secondary_text: result.formatted_address
            }
          }));
          setSuggestions(suggestions);
          setShowSuggestions(true);
        } else {
          setSuggestions([]);
          setShowSuggestions(false);
        }
      });
    } catch (error) {
      console.error('Error fetching suggestions:', error);
      setIsLoading(false);
    }
  };

  const handleSuggestionClick = (prediction: any) => {
    // Use geocoding to get coordinates for the selected address
    const geocoder = new google.maps.Geocoder();
    geocoder.geocode({ placeId: prediction.place_id }, (results: any, status: any) => {
      if (status === 'OK' && results && results.length > 0) {
        const result = results[0];
        const lat = result.geometry.location.lat();
        const lng = result.geometry.location.lng();
        const address = result.formatted_address;
        
        const location = { latitude: lat, longitude: lng, address };

        setSelectedLocation(location);
        setSearchQuery(location.address);
        setShowSuggestions(false);
        onLocationSelectRef.current(location);
        
        // Update marker position on map directly
        if (marker.current) {
          marker.current.setPosition({ lat, lng });
        } else {
          marker.current = new google.maps.Marker({
            position: { lat, lng },
            map: mapInstance.current,
            draggable: true,
            title: 'Selected Location',
          });

          // Add drag listener to marker
          marker.current.addListener('dragend', (event: google.maps.MapMouseEvent) => {
            if (event.latLng) {
              const newLat = event.latLng.lat();
              const newLng = event.latLng.lng();
              
              const location = { 
                latitude: newLat, 
                longitude: newLng, 
                address: `${newLat.toFixed(6)}, ${newLng.toFixed(6)}` 
              };
              setSelectedLocation(location);
              setSearchQuery(location.address);
              onLocationSelectRef.current(location);
            }
          });
        }

        // Center map on marker
        if (mapInstance.current) {
          mapInstance.current.setCenter({ lat, lng });
        }
      }
    });
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchQuery(value);
    
    if (value.trim()) {
      handleSearch(value);
    } else {
      setSuggestions([]);
      setShowSuggestions(false);
    }
  };

  const clearSelection = () => {
    setSearchQuery('');
    setSelectedLocation(null);
    setSuggestions([]);
    setShowSuggestions(false);
    // Don't call onLocationSelect with invalid coordinates
    // onLocationSelect({ latitude: 0, longitude: 0, address: '' });
    
    // Remove marker from map
    if (marker.current) {
      marker.current.setMap(null);
      marker.current = null;
    }
  };

  // Cleanup function
  useEffect(() => {
    return () => {
      // Clean up marker when component unmounts
      if (marker.current) {
        marker.current.setMap(null);
        marker.current = null;
      }
      // Reset initialization flag
      isInitialized.current = false;
    };
  }, []);

  const handleInputFocus = () => {
    if (suggestions.length > 0) {
      setShowSuggestions(true);
    }
  };

  const handleInputBlur = () => {
    // Delay hiding suggestions to allow clicking on them
    setTimeout(() => setShowSuggestions(false), 200);
  };

  return (
    <div className="space-y-4">
      {/* Search Input */}
      <div className="relative">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
          <input
            type="text"
            value={searchQuery}
            onChange={handleInputChange}
            onFocus={handleInputFocus}
            onBlur={handleInputBlur}
            placeholder={placeholder || t('itineraries.fields.searchLocation')}
            className="w-full pl-10 pr-10 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 text-sm"
          />
          {selectedLocation && (
            <button
              type="button"
              onClick={clearSelection}
              className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>

        {/* Loading indicator */}
        {isLoading && (
          <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
          </div>
        )}

        {/* Suggestions dropdown */}
        {showSuggestions && suggestions.length > 0 && (
          <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-y-auto">
            {suggestions.map((suggestion, index) => (
              <button
                key={index}
                type="button"
                onClick={() => handleSuggestionClick(suggestion)}
                className="w-full px-4 py-2 text-left hover:bg-gray-100 focus:bg-gray-100 focus:outline-none text-sm"
              >
                <div className="flex items-center">
                  <MapPin className="h-4 w-4 text-gray-400 mr-2 flex-shrink-0" />
                  <div>
                    <div className="font-medium text-gray-900">{suggestion.structured_formatting?.main_text}</div>
                    {suggestion.structured_formatting?.secondary_text && (
                      <div className="text-gray-500 text-xs">{suggestion.structured_formatting.secondary_text}</div>
                    )}
                  </div>
                </div>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Map Display */}
      {showMap && (
        <div className="border border-gray-300 rounded-md overflow-hidden">
          <div className="bg-gray-50 px-3 py-2 border-b border-gray-200">
            <div className="flex items-center text-sm text-gray-600">
              <Navigation className="h-4 w-4 mr-2" />
              <span>{t('itineraries.fields.mapInstructions')}</span>
            </div>
          </div>
          <div 
            ref={mapRef} 
            className="w-full bg-gray-100"
            style={{ height: mapHeight, minHeight: '200px' }}
          />
        </div>
      )}

      {/* Selected location display */}
      {selectedLocation && (
        <div className="p-3 bg-green-50 border border-green-200 rounded-md">
          <div className="flex items-center text-sm text-green-800">
            <MapPin className="h-4 w-4 mr-2" />
            <span className="font-medium">{selectedLocation.address}</span>
          </div>
          <div className="text-xs text-green-600 mt-1">
            {selectedLocation.latitude.toFixed(6)}, {selectedLocation.longitude.toFixed(6)}
          </div>
        </div>
      )}
    </div>
  );
};
